/**
 * Detects whether the app is running inside a TWA or mobile browser.
 * Used to decide whether to attempt the Web Share API vs anchor-click downloads.
 */
export function isMobileOrTWA() {
  return (
    /android/i.test(navigator.userAgent) ||
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    window.matchMedia('(display-mode: standalone)').matches
  );
}

/**
 * Saves or shares a file in a cross-platform way.
 *
 * - On Android/TWA: opens the native share sheet (Web Share API)
 * - On desktop: triggers an anchor-click download
 * - If the user cancels the share sheet, resolves silently with no error toast
 *
 * @param {Blob} blob - The file content
 * @param {string} filename - The suggested filename including extension
 * @param {string} mimeType - The MIME type of the file
 */
export async function saveOrShareFile(blob, filename, mimeType) {
  const file = new File([blob], filename, { type: mimeType });

  // Attempt Web Share API first — works correctly on Android and iOS
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({
        files: [file],
        title: filename,
      });
      return; // Share sheet handled it — done
    } catch (err) {
      // AbortError means the user dismissed the share sheet — not an error
      if (err.name === 'AbortError') return;
      // Any other error falls through to the anchor-click fallback below
      console.warn('Web Share API failed, falling back to download:', err);
    }
  }

  // Fallback: anchor-click download — works on desktop browsers
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}
